<script setup>
import { computed } from 'vue'
import { KkModal, KkButton } from '../../base'
import { KkInfo } from '../../ui'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false,
  },
  categoryName: {
    type: String,
  },
  storeUuids: {
    type: Array,
    required: true,
  },
  selectedStores: {
    type: Array,
    required: true,
  },
  images: {
    type: Array,
    required: true,
  }
})
const emit = defineEmits(['on-cancel', 'on-submit'])

const hasService = computed(() => {
  return props.selectedStores.some((store) => store.services.length)
})

const serviceName = (service) => {
  switch (service) {
  case 'google_map':
    return t('labels.sites.google')
  case 'yahoo_map':
    return t('labels.sites.yahooMap')
  default:
    return t(`labels.sites.${service}`)
  }
}
</script>

<template>
  <KkModal :is-visible="isVisible">
    <div class="content__header">
      {{ t('components.MediaLibraryUploadConfirmModal.header', { number: images.length }) }}
    </div>
    <div class="content__body">
      <div class="images">
        <div v-for="image in images" :key="image.id" class="imageWrapper">
          <img :src="image.url" class="image">
        </div>
      </div>
      <div class="storeCountSection">
        <p v-if="storeUuids.length !== 0" class="storeCounts">
          {{ t('components.MediaLibraryUploadConfirmModal.storeCount', { number: storeUuids.length }) }}
        </p>
        <template v-else>
          <p class="noSelectStore">
            {{ t('components.MediaLibraryUploadConfirmModal.noSelectStore') }}
          </p>
          <KkInfo>
            {{ t('components.MediaLibraryUploadConfirmModal.noSelectStoreInfo') }}
          </KkInfo>
        </template>
      </div>
      <div v-if="storeUuids.length !== 0" class="p-table__content">
        <table>
          <thead>
            <tr>
              <th>{{ t('components.MediaLibraryUploadConfirmModal.table.header.store') }}</th>
              <th v-if="hasService">{{ t('components.MediaLibraryUploadConfirmModal.table.header.category') }}</th>
              <th v-if="hasService">{{ t('components.MediaLibraryUploadConfirmModal.table.header.service') }}</th>
            </tr>
            <tr class="p-table__content__space"><td :colspan="hasService ? 3 :2" /></tr>
          </thead>
          <tbody class="stores">
            <template v-for="(store, i) in selectedStores" :key="store.uuid">
              <tr :class="i === selectedStores.length - 1 ? 'stores__last' : undefined">
                <td :rowspan="hasService ? store.services.length : undefined" class="tableHead">
                  {{ store.name }}
                </td>
                <td :rowspan="hasService ? store.services.length : undefined">
                  {{ categoryName }}
                </td>
                <td v-if="hasService">{{ serviceName(store.services[0]) }}</td>
              </tr>
              <template v-if="store.services.length > 1">
                <template v-for="(service, j) in store.services" :key="service">
                  <tr v-if="j > 0">
                    <td>{{ serviceName(service) }}</td>
                  </tr>
                </template>
              </template>
            </template>
          </tbody>
        </table>
      </div>
    </div>
    <div class="content__footer">
      <KkButton
        class="footerCancelBtn"
        color="secondary"
        :text="$t('labels.cancel')"
        @click="emit('on-cancel')"
      />
      <KkButton
        color="primary"
        :text="$t('labels.confirm')"
        @click="emit('on-submit')"
      />
    </div>
  </KkModal>
</template>

<style lang="scss" scoped>
@import "../../../../../stylesheets/biz_spa/foundation/_var.scss";
.content {
  &__header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    padding: 20px 32px;
    border-bottom: 1px solid $color-base-c;
    font-size: 16px;
    line-height: 1.5;
  }
  &__footer {
    display: flex;
    justify-content: flex-end;
    padding: 32px;
    border-top: 1px solid $color-base-c;
  }
  &__body {
    height: 50vh;
    padding: 32px;
    overflow-y: scroll;
    .item {
      margin: 10px 0;
      padding: 18px;
      border: 1px solid #E4E7EB;
      border-radius: 4px;
    }
  }
}
.stores {
  &__last {
    td:first-child,
    td:nth-child(2) {
      border-bottom: 1px solid $color-base-c;
    }
    td:first-child {
      border-bottom-left-radius: 4px;
    }
    &:last-of-type {
      td:first-child:not([rowspan="1"]) {
        border-bottom-left-radius: 0;
      }
    }
    + tr td:first-child {
      border-bottom-left-radius: 0 !important;
    }
  }
}
.footerCancelBtn {
  margin-right: 8px;
}
.images {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
}
.image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.imageWrapper {
  width: 190px;
  height: 190px;
}
.storeCountSection {
  margin: 24px 0;
}
.storeCounts {
  font-weight: bold;
}
.noSelectStore {
  margin-bottom: 16px;
  font-weight: bold;
}
.tableHead {
  background-color: #F8F9FA;
  &:not(:first-child) {
    border-left: 0;
  }
}
</style>
